import { useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { PackagePlus, Pencil, Trash2, Check, X } from "lucide-react";
import { Panel, Inp, Sel, Btn, Th, Td, RT } from "./ui";

const EV_FIELDS = [["item_number", "Item Number"], ["description", "Description"],
  ["category", "Category", ["Weapon", "Drugs/Narcotics", "Currency", "Documents", "Electronics", "Clothing", "Biological", "Photograph/Media", "Other"]],
  ["quantity", "Quantity"], ["collected_by", "Collected By"], ["collected_at", "Date/Time Collected"],
  ["location_collected", "Location Collected"], ["packaging", "Packaging"], ["storage_location", "Storage Location"],
  ["disposition", "Disposition", ["", "Held as Evidence", "Returned to Owner", "Destroyed", "Transferred", "Pending"]]];
const PR_FIELDS = [["category", "Category", ["Found", "Lost", "Stolen", "Recovered", "Damaged", "Safekeeping", "Evidence"]],
  ["description", "Item Description"], ["owner", "Owner"], ["serial", "Serial Number"], ["value", "Estimated Value"],
  ["condition", "Condition"], ["location", "Location"], ["disposition", "Disposition"]];

function Mod({ sub, fields, key_, title, cols, rep, canEdit, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState({});
  const rows = rep[sub] || [];
  const save = async () => {
    try {
      if (editing?.[key_]) await client.patch(`/rms-reports/${rep.id}/records/${sub}/${editing[key_]}`, f);
      else await client.post(`/rms-reports/${rep.id}/records/${sub}`, f);
      toast.success("Saved"); setEditing(null); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const remove = async (r) => {
    if (!window.confirm("Remove record? (audited)")) return;
    try { await client.delete(`/rms-reports/${rep.id}/records/${sub}/${r[key_]}`); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
  };
  return (
    <Panel title={`${title} (${rows.length})`} testid={`panel-${sub}`}
      right={canEdit && <Btn small onClick={() => { setEditing({}); setF({}); }} testid={`${sub}-add-btn`}><PackagePlus size={11} /> Add</Btn>}>
      {rows.length === 0 && <div className="font-mono text-[11px] py-3 text-center" style={{ color: RT.dim }}>NO {title.toUpperCase()} RECORDED</div>}
      {rows.length > 0 && (
        <table className="w-full">
          <thead><tr>{cols.map(([h]) => <Th key={h}>{h}</Th>)}{canEdit && <Th> </Th>}</tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r[key_]} data-testid={`${sub}-row-${r[key_]}`}>
              {cols.map(([h, fn]) => <Td key={h}>{fn(r) || "—"}</Td>)}
              {canEdit && <Td><div className="flex gap-1.5">
                <button data-testid={`${sub}-edit-${r[key_]}`} onClick={() => { setEditing(r); setF(r); }} className="text-[#1d5d90]"><Pencil size={13} /></button>
                <button data-testid={`${sub}-del-${r[key_]}`} onClick={() => remove(r)} className="text-[#b91c1c]"><Trash2 size={13} /></button>
              </div></Td>}
            </tr>))}
          </tbody>
        </table>
      )}
      {sub === "evidence" && rows.some((r) => (r.chain || []).length) && (
        <div className="mt-2 border-t pt-2" style={{ borderColor: RT.border }}>
          <div className="font-mono text-[9px] uppercase mb-1" style={{ color: RT.dim }}>Chain of Custody</div>
          {rows.map((r) => (r.chain || []).map((c, i) => (
            <div key={r[key_] + i} className="font-mono text-[10px]" style={{ color: RT.text }}>
              {r.evidence_number}: {c.ts?.slice(0, 16)} · {c.user} · {c.action} {c.note ? `— ${c.note}` : ""}
            </div>)))}
        </div>
      )}
      {editing !== null && (
        <div className="border rounded-sm p-3 mt-2" style={{ borderColor: RT.head, background: "#eef4f9" }}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {fields.map(([k, label, opts]) => opts
              ? <Sel key={k} label={label} value={f[k]} onChange={(v) => setF({ ...f, [k]: v })} options={opts.filter(Boolean)} testid={`${sub}-f-${k}`} />
              : <Inp key={k} label={label} value={f[k]} onChange={(v) => setF({ ...f, [k]: v })} testid={`${sub}-f-${k}`} />)}
          </div>
          <div className="flex gap-2 mt-2">
            <Btn onClick={save} testid={`${sub}-editor-save`}><Check size={12} /> Save</Btn>
            <Btn onClick={() => setEditing(null)} color="#64748b" testid={`${sub}-editor-cancel`}><X size={12} /> Cancel</Btn>
          </div>
        </div>
      )}
    </Panel>
  );
}

export default function SectionEvidence({ rep, canEdit, onChanged, only }) {
  return (
    <div className="space-y-3">
      {(!only || only === "evidence") && <Mod sub="evidence" key_="eid" title="Evidence" fields={EV_FIELDS} rep={rep} canEdit={canEdit} onChanged={onChanged}
        cols={[["EVIDENCE #", (e) => e.evidence_number], ["DESCRIPTION", (e) => (e.description || "").slice(0, 40)],
          ["CATEGORY", (e) => e.category], ["QTY", (e) => e.quantity], ["COLLECTED BY", (e) => e.collected_by],
          ["STORAGE", (e) => e.storage_location], ["DISPOSITION", (e) => e.disposition]]} />}
      {(!only || only === "property") && <Mod sub="property" key_="prid" title="Property" fields={PR_FIELDS} rep={rep} canEdit={canEdit} onChanged={onChanged}
        cols={[["CATEGORY", (p) => p.category], ["DESCRIPTION", (p) => (p.description || "").slice(0, 40)],
          ["OWNER", (p) => p.owner], ["SERIAL", (p) => p.serial], ["VALUE", (p) => p.value],
          ["CONDITION", (p) => p.condition], ["DISPOSITION", (p) => p.disposition]]} />}
    </div>
  );
}
